/* eslint-disable jsx-a11y/no-autofocus */

import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod/dist/zod';

import { CreditCard, FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import paymentCardsLocales from 'common/paymentCards/locales';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';

import {
  CARD_ALIAS_MAX_LENGTH,
  CardAliasFormSchema,
  cardAliasFormSchema,
} from './cardAliasForm.schema';
import { useUpdateCreditCardAlias } from './hooks/useUpdateCreditCardAlias';
import locales from './locales';

type CardAliasProps = Pick<
  CreditCard,
  'alias' | 'cardHolderName' | 'creditCardId' | 'isPhysical'
>;

const CardAlias: FC<CardAliasProps> = ({
  alias,
  cardHolderName,
  creditCardId,
  isPhysical,
}) => {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { showToast } = useToast();

  const [isEditing, setEditing] = useState(false);

  const canAliasCard = canAccessFeature({
    name: FeatureName.Paymentcards,
  });

  const getDefaultCardAlias = (anAlias = alias) => {
    const defaultCardAlias = anAlias?.trim();
    if (defaultCardAlias) {
      return defaultCardAlias;
    }

    if (canAliasCard) {
      return cardHolderName;
    }

    const cardMedium = isPhysical ? 'physicalCard' : 'virtualCard';
    return paymentCardsLocales[cardMedium];
  };

  const { formState, handleSubmit, register, reset, watch } =
    useForm<CardAliasFormSchema>({
      defaultValues: {
        alias: getDefaultCardAlias(),
      },
      mode: 'onChange',
      resolver: zodResolver(cardAliasFormSchema),
    });

  const { updateCreditCardAlias } = useUpdateCreditCardAlias();

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        const updatedCardAlias = values.alias?.trim() ?? null;
        await updateCreditCardAlias(creditCardId, updatedCardAlias);
        setEditing(false);
        reset({ alias: getDefaultCardAlias(updatedCardAlias) });
        showToast({
          message: locales.cardAliasSuccessfullyUpdated,
          type: 'success',
        });
      })}
    >
      <SunshineCard.Group isBoxed={false}>
        <SunshineCard
          onClick={() => {
            setEditing(true);
          }}
        >
          <SunshineCard.Slot name="aside">
            <SquircleIcon name="tag" />
          </SunshineCard.Slot>
          <SunshineCard.Content>
            <SunshineCard.Title>{locales.cardAlias}</SunshineCard.Title>
            {isEditing ? (
              <Form.Field
                error={formState.errors.alias?.message}
                hint={`${
                  watch('alias')?.length ?? 0
                } / ${CARD_ALIAS_MAX_LENGTH} ${locales.maximumCharacters}`}
              >
                <Form.Input
                  {...register('alias')}
                  autoFocus
                  maxLength={CARD_ALIAS_MAX_LENGTH}
                />
              </Form.Field>
            ) : (
              <Text>{getDefaultCardAlias()}</Text>
            )}
          </SunshineCard.Content>

          <SunshineCard.Slot name="action">
            {isEditing ? (
              <Button
                isDisabled={!!formState.errors.alias?.message}
                minWidth="inherit"
                size="sm"
                type="submit"
                variant="inline-secondary"
              >
                {locales.ok}
              </Button>
            ) : (
              <SunshineCard.SunshineIcon name="edit" />
            )}
          </SunshineCard.Slot>
        </SunshineCard>
      </SunshineCard.Group>
    </form>
  );
};

export default CardAlias;
